import { el, mount, unmount, setAttr } from 'redom';

import application from 'application';

import { stringToSVG } from 'utilities';
import { preventPropogation } from 'utilities/dom';

import * as deviceAccessStatuses from 'constants/device-access-statuses';
import externalEventCodes from 'event-system/codes/external';

import Tooltip from './tooltip';

import microphoneInactiveIconString from 'assets/images/microphone-inactive-icon.svg?raw';
import microphoneActiveIconString from 'assets/images/microphone-active-icon.svg?raw';

const microphoneInactiveIcon = stringToSVG(microphoneInactiveIconString);
const microphoneActiveIcon = stringToSVG(microphoneActiveIconString);

class AudioControls {

  constructor(muted = true, currentUserId, meeting) {

    this.stream = null;

    this.currentUserId = currentUserId;
    this.meeting = meeting;

    this.muted = this.getMutedState();
    this.unmuteable = this.getUnmuteableStatus();

    this.muteButton = el('button.meeting-audio-mute-button.meeting-room-control', [microphoneInactiveIcon.cloneNode(true)]);
    this.unmuteButton = el('button.meeting-audio-unmute-button.meeting-room-control', {
      disabled: this.unmuteable
    }, [microphoneActiveIcon.cloneNode(true)]);

    this.muteButtonWithTooltip = new Tooltip(this.muteButton, '.mute-button-tooltip', 'Mute audio');
    this.unmuteButtonWithTooltip = new Tooltip(this.unmuteButton, '.unmute-button-tooltip', 'Unmute audio');

    this.el = el('.screen-audio-controls-main');

    if (this.muted === true) {
      this.mountUnmuteButton();
    } else {
      this.mountMuteButton();
    }

  }

  onmount() {

    this.registerEventListeners();

  }

  onunmount() {

    this.unregisterEventListeners();
    application.devicesManager.releaseMicrophoneStream();

  }

  registerForMeetingMutedEvent(postId) {

    function handleMeetingMutedEvent(data) {
      this.disableUnmute();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_MUTED}:${postId}`, handleMeetingMutedEvent.bind(this));

  }

  registerForMeetingUnmutedEvent(postId) {

    function handleMeetingUnmutedEvent(data) {

      this.enableUnmute();

    }

    application.eventSystem.internal.eventFor(this, `${externalEventCodes.SINGLE_PRODUCER_MEETING_UNMUTED}:${postId}`, handleMeetingUnmutedEvent.bind(this));

  }

  unregisterEventListeners() {

    // TODO: For viewers, this is not getting called & so event handlers are consuming memory
    // eslint-disable-next-line no-console
    console.log('All the event handlers are removed');
    application.eventSystem.internal.offAllOn(this);

  }

  isAuthorized() {

  }

  registerEventListeners() {

    this.muteButton.addEventListener('click', this.mute.bind(this));
    this.muteButton.addEventListener('dblclick', preventPropogation);

    this.unmuteButton.addEventListener('click', this.unmute.bind(this));
    this.unmuteButton.addEventListener('dblclick', preventPropogation);

    const postId = this.meeting.postId;

    this.registerForMeetingMutedEvent(postId);
    this.registerForMeetingUnmutedEvent(postId);

  }

  mountMuteButton() {

    mount(this.el, this.muteButtonWithTooltip);

  }

  unmountMuteButton() {

    unmount(this.el, this.muteButtonWithTooltip);

  }

  mountUnmuteButton() {

    mount(this.el, this.unmuteButtonWithTooltip);

  }

  unmountUnmuteButton() {

    unmount(this.el, this.unmuteButtonWithTooltip);

  }

  doMute() {

    if (this.stream !== null) {

      const tracks = this.stream.getTracks();

      for (let track of tracks) {
        track.stop();
      }

      this.stream = null;
    }

    this.muted = true;

    const microphoneMutedEvent = new CustomEvent('muted');

    this.el.dispatchEvent(microphoneMutedEvent);

    this.unmountMuteButton();
    this.mountUnmuteButton();

  }

  async mute(event) {

    event.stopPropagation();

    this.doMute();

  }

  async doUnmute() {

    try {

      const stream = this.stream = await application.devicesManager.getMicrophoneStream();

      this.muted = false;

      const microphoneUnmutedEvent = new CustomEvent('unmuted', {
        detail: {
          stream
        }
      });

      this.el.dispatchEvent(microphoneUnmutedEvent);

      this.unmountUnmuteButton();
      this.mountMuteButton();

    } catch (exception) {

      application.devicesManager.ensureMediaPermissions(exception.message, deviceAccessStatuses.ALLOWED);

    }

  }

  async unmute(event) {

    event.stopPropagation();

    this.doUnmute();

  }

  getMutedState() {

    const meeting = this.meeting;

    if (this.currentUserId === meeting.ownerId) {
      return true;
    }

    return meeting.muteAll || true;

  }

  getUnmuteableStatus() {

    const meeting = this.meeting;

    if (this.currentUserId === meeting.ownerId) {
      return false;
    }

    return meeting.muteAll;

  }

  disableUnmute() {

    const meeting = this.meeting;

    if (this.currentUserId === meeting.ownerId) {
      return true;
    }

    meeting.muteAll = true;

    setAttr(this.unmuteButton, { disabled: true });

    if (this.muted !== true) {
      this.doMute();
    }

  }

  enableUnmute() {

    const meeting = this.meeting;

    if (this.currentUserId === meeting.ownerId) {
      return true;
    }

    setAttr(this.unmuteButton, { disabled: false });

  }

}

export default AudioControls;